import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { IconCommand, IconSearch, IconX } from '@/icons';
import { api, errorMessage } from '@/lib/api';
import { avatarGradient, initials } from '@/lib/format';
import { qk } from '@/lib/query';
import { useAuthStore } from '@/store/auth';
import { useUiStore } from '@/store/ui';

/**
 * Глобальный поиск (⌘K / Ctrl+K).
 *
 * Разметка из старой панели: `.modal-backdrop` + `.cmdk-modal`, строки
 * `.cmdk-row`. Флаг открытия поднимает каркас (AppShell → useUiStore), сам
 * диалог рисуется здесь.
 *
 * Три отличия от предшественника, все — следствие нового API:
 *
 *   1. Поиск идёт СРАЗУ ПО ВСЕМ доступным проектам: `projectId` в запрос не
 *      передаём. Оператору кидают имя клиента, не уточняя проект, и заставлять
 *      его сначала переключиться — ровно та работа, ради которой поиск и есть.
 *      Поэтому при переходе по результату проект переключается сам.
 *   2. Подсветка рисуется по КООРДИНАТАМ с сервера (`matchOffset`), а не
 *      повторным поиском подстроки на клиенте и не innerHTML. Регистр,
 *      многоточия по краям и Юникод сервер уже учёл; второй поиск на клиенте
 *      неизбежно разошёлся бы с ним — а `dangerouslySetInnerHTML` на чужом
 *      тексте вдобавок открыл бы дорогу разметке из сообщения.
 *   3. Кроме переписки находятся контакты. У контакта без переписки перехода
 *      нет — такую строку показываем, но не открываем.
 */

// ──────────────────────────── Формы ответа ────────────────────────────

/*
 * Объявлены здесь, а не в lib/types.ts: тех DTO нет ни в @support/shared, ни в
 * ядре фронта — они живут в apps/api/src/modules/content/search.dto.ts, и
 * единственный их потребитель на клиенте — этот файл.
 */

type HitAuthor = 'contact' | 'operator' | 'system' | 'automation';

/** Многоточия по краям сервер НЕ вклеивает: они сдвинули бы matchOffset. */
interface SnippetDto {
  text: string;
  matchOffset: number;
  matchLength: number;
  truncatedStart: boolean;
  truncatedEnd: boolean;
}

interface MessageHitDto {
  kind: 'message';
  messageId: string;
  conversationId: string;
  projectId: string;
  contactId: string;
  title: string;
  username: string | null;
  from: HitAuthor;
  direction: 'IN' | 'OUT';
  snippet: SnippetDto;
  at: string;
}

interface ContactHitDto {
  kind: 'contact';
  contactId: string;
  projectId: string;
  /** Последний диалог контакта; null — контакт без переписки. */
  conversationId: string | null;
  title: string;
  username: string | null;
  externalId: string | null;
  snippet: SnippetDto;
  at: string;
}

type SearchHitDto = MessageHitDto | ContactHitDto;

interface SearchResponseDto {
  query: string;
  hits: SearchHitDto[];
}

/** SEARCH_QUERY_MIN на бэкенде: с одним символом запрос вернёт 400. */
const MIN_QUERY = 2;

/** Пауза перед запросом. 220 мс — как в старой панели. */
const DEBOUNCE_MS = 220;

const AUTHOR_LABEL: Record<HitAuthor, string> = {
  contact: 'от клиента',
  operator: 'от оператора',
  system: 'системное',
  automation: 'автоответ',
};

// ──────────────────────────── Компонент ────────────────────────────

export default function GlobalSearch(): ReactNode {
  const open = useUiStore((state) => state.searchOpen);
  const setSearchOpen = useUiStore((state) => state.setSearchOpen);

  // Всё состояние поиска живёт во внутреннем компоненте: он размонтируется
  // вместе с диалогом, и запрос набранного в прошлый раз не висит в фоне.
  if (!open) return null;
  return <SearchDialog onClose={() => setSearchOpen(false)} />;
}

function SearchDialog({ onClose }: { onClose: () => void }): ReactNode {
  const navigate = useNavigate();
  const projects = useAuthStore((state) => state.projects);
  const currentProjectId = useAuthStore((state) => state.currentProjectId);
  const setCurrentProject = useAuthStore((state) => state.setCurrentProject);

  const [text, setText] = useState('');
  const [term, setTerm] = useState('');
  const [cursor, setCursor] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Запрос уходит не на каждое нажатие: у поиска свой лимит в 120 запросов на
  // минуту, и без паузы его съедает одна минута набора текста втроём.
  useEffect(() => {
    const trimmed = text.trim();
    const timer = window.setTimeout(() => setTerm(trimmed), DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [text]);

  const enabled = term.length >= MIN_QUERY;
  const { data, isFetching, isError, error } = useQuery({
    queryKey: qk.search(term, null),
    queryFn: () => api.get<SearchResponseDto>('/search', { query: { q: term } }),
    enabled,
    // Выдача устаревает мгновенно: между двумя открытиями палитры переписка
    // успевает измениться, а поиск — это всегда «что там прямо сейчас».
    staleTime: 0,
  });

  const hits = useMemo(() => (enabled ? (data?.hits ?? []) : []), [enabled, data]);

  // Список сменился — курсор на первую строку: он мог указывать в пустоту.
  useEffect(() => {
    setCursor(0);
  }, [hits]);

  // Держим выделенную строку в поле зрения при навигации стрелками.
  useEffect(() => {
    const node = listRef.current?.querySelector<HTMLElement>('.cmdk-row.active');
    node?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  const pick = (hit: SearchHitDto | undefined) => {
    if (!hit?.conversationId) return;
    // Проект переключаем ДО перехода: список и карточка диалога читают
    // currentProjectId, и без этого экран открылся бы с фильтрами чужого
    // проекта — то есть пустым.
    if (hit.projectId !== currentProjectId) setCurrentProject(hit.projectId);
    onClose();
    void navigate(`/inbox/${hit.conversationId}`);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((value) => Math.min(value + 1, hits.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((value) => Math.max(value - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      pick(hits[cursor]);
    }
  };

  // Код проекта в строке результата нужен только тем, у кого проектов больше
  // одного: остальным это шум в каждой строке.
  const projectCode = (projectId: string): string | null => {
    if (projects.length < 2) return null;
    return projects.find((project) => project.id === projectId)?.code ?? null;
  };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="cmdk-modal" role="dialog" aria-modal="true" aria-label="Глобальный поиск">
        <div className="cmdk-head">
          <IconSearch size={16} />
          <input
            ref={inputRef}
            placeholder="Найти диалог, контакт или сообщение…"
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={onKeyDown}
          />
          <span className="cmdk-tag">
            <IconCommand size={11} /> K
          </span>
          <button type="button" className="icon-btn" onClick={onClose} title="Закрыть (Esc)">
            <IconX size={16} />
          </button>
        </div>

        <div className="cmdk-list" ref={listRef}>
          {!enabled && (
            <div className="cmdk-hint">
              Введите минимум {MIN_QUERY} символа — ищем по именам, @username и тексту переписки
              во всех ваших проектах
            </div>
          )}
          {enabled && isFetching && hits.length === 0 && <div className="cmdk-hint">Ищем…</div>}
          {/* Отказ поиска (свой лимит в 120 запросов, 500, обрыв связи) нельзя
              выдавать за пустую выдачу: «ничего не найдено» — утверждение о
              данных, и оператор скажет клиенту, что обращения не было. */}
          {enabled && isError && (
            <div className="cmdk-hint">{errorMessage(error, 'Поиск недоступен')}</div>
          )}
          {enabled && !isFetching && !isError && hits.length === 0 && (
            <div className="cmdk-hint">Ничего не найдено</div>
          )}

          {hits.map((hit, index) => (
            <button
              type="button"
              key={hit.kind === 'message' ? hit.messageId : `contact:${hit.contactId}`}
              className={`cmdk-row ${index === cursor ? 'active' : ''}`}
              onMouseEnter={() => setCursor(index)}
              onClick={() => pick(hit)}
              disabled={hit.conversationId === null}
            >
              <span
                className="cmdk-av"
                style={{ background: avatarGradient(hit.contactId) }}
                aria-hidden
              >
                {initials({ displayName: hit.title, username: hit.username })}
              </span>
              <span className="cmdk-body">
                <span className="cmdk-name">{hit.title}</span>
                <span className="cmdk-snippet">
                  <Highlighted snippet={hit.snippet} />
                </span>
              </span>
              <span className="cmdk-dir">{trailing(hit, projectCode(hit.projectId))}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

/** Правая подпись строки: проект (если их несколько) и кто это писал. */
function trailing(hit: SearchHitDto, code: string | null): string {
  const what =
    hit.kind === 'message'
      ? AUTHOR_LABEL[hit.from]
      : hit.conversationId === null
        ? 'контакт без переписки'
        : 'контакт';
  return code ? `${code} · ${what}` : what;
}

/**
 * Фрагмент с подсвеченным совпадением.
 *
 * Режем по координатам сервера. `<mark>` — тот же тег, под который написан
 * `.cmdk-snippet mark` в index.css.
 */
function Highlighted({ snippet }: { snippet: SnippetDto }): ReactNode {
  const { text, matchOffset, matchLength, truncatedStart, truncatedEnd } = snippet;

  const prefix = truncatedStart ? '…' : '';
  const suffix = truncatedEnd ? '…' : '';

  // Совпадения в тексте нет (нашлось по переводу или по другому полю) — просто
  // показываем начало фразы.
  if (matchLength <= 0) {
    return (
      <>
        {prefix}
        {text}
        {suffix}
      </>
    );
  }

  const start = Math.max(0, Math.min(matchOffset, text.length));
  const end = Math.max(start, Math.min(start + matchLength, text.length));

  return (
    <>
      {prefix}
      {text.slice(0, start)}
      <mark>{text.slice(start, end)}</mark>
      {text.slice(end)}
      {suffix}
    </>
  );
}
